// Loan Compliance Module v1.0 PR 3 — Material Event Register table for a
// single loan. Capture button is gated by Compliance.ManageMaterialEvents;
// row-click opens the per-event drawer.

import { useState } from "react";
import { Plus } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/design-system/components/ui";
import type { MaterialEventDto } from "@/api/compliance/types";
import { useMaterialEvents } from "@/hooks/compliance/useCompliance";
import { formatDate } from "../common/colorBand";
import { CaptureMaterialEventModal } from "./CaptureMaterialEventModal";
import { MaterialEventDrawer } from "./MaterialEventDrawer";
import {
  MATERIAL_EVENT_DISPOSITION_LABELS,
  MATERIAL_EVENT_SOURCE_LABELS,
  MATERIAL_EVENT_TYPE_LABELS,
} from "./labels";

interface MaterialEventsSectionProps {
  loanId: string;
  canCapture: boolean;
  canManage: boolean;
}

function severityBadge(sev: string) {
  if (sev === "Critical") return "error" as const;
  if (sev === "Material") return "review" as const;
  if (sev === "Process") return "info" as const;
  return "archived" as const;
}

export function MaterialEventsSection({
  loanId,
  canCapture,
  canManage,
}: MaterialEventsSectionProps) {
  const { data, isLoading } = useMaterialEvents(loanId);
  const events = data ?? [];

  const [captureOpen, setCaptureOpen] = useState(false);
  const [openEventId, setOpenEventId] = useState<string | null>(null);
  const openEvent: MaterialEventDto | null = openEventId
    ? events.find((e) => e.id === openEventId) ?? null
    : null;

  return (
    <Card>
      <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-3">
        <div>
          <h3 className="font-display font-bold text-h3 m-0">
            Material Event Register
          </h3>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            BLA §12.4 / §4.5 — all material events captured against this loan.
          </p>
        </div>
        {canCapture && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setCaptureOpen(true)}
            data-testid="material-event-capture-button"
          >
            <Plus size={14} aria-hidden /> Capture event
          </Button>
        )}
      </div>

      {isLoading ? (
        <p className="text-small text-gray-mid italic m-0">Loading events…</p>
      ) : events.length === 0 ? (
        <p
          className="text-small text-gray-mid m-0"
          data-testid="material-events-empty"
        >
          No material events captured.
        </p>
      ) : (
        <Table data-testid="material-events-table">
          <THead>
            <TR>
              <TH>Type</TH>
              <TH>Source</TH>
              <TH>Severity</TH>
              <TH>Detected</TH>
              <TH>Disposition</TH>
            </TR>
          </THead>
          <TBody>
            {events.map((e) => (
              <TR
                key={e.id}
                className="cursor-pointer hover:bg-white-warm"
                onClick={() => setOpenEventId(e.id)}
                data-testid={`material-event-row-${e.id}`}
              >
                <TD>
                  <div className="font-semibold">{e.title}</div>
                  <div className="text-caption text-gray-mid">
                    {MATERIAL_EVENT_TYPE_LABELS[e.eventType]}
                  </div>
                </TD>
                <TD>{MATERIAL_EVENT_SOURCE_LABELS[e.source]}</TD>
                <TD>
                  <Badge status={severityBadge(e.severity)}>{e.severity}</Badge>
                </TD>
                <TD>{formatDate(e.dateDetected)}</TD>
                <TD>
                  {MATERIAL_EVENT_DISPOSITION_LABELS[e.disposition] ??
                    e.disposition}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {captureOpen && (
        <CaptureMaterialEventModal
          loanId={loanId}
          open={captureOpen}
          onClose={() => setCaptureOpen(false)}
        />
      )}
      {openEvent && (
        <MaterialEventDrawer
          loanId={loanId}
          event={openEvent}
          canManage={canManage}
          open={!!openEvent}
          onClose={() => setOpenEventId(null)}
        />
      )}
    </Card>
  );
}
